import React from "react";
import { motion } from "framer-motion";
import { DollarSign, Lightbulb } from "lucide-react";

const normalizeSoftwarePricingCopy = (text, softwarePricingMode) => {
  if (!text) {
    return text;
  }

  if (softwarePricingMode === "permanent_free") {
    return text
      .replace(
        /Included free for the first 12 months on your plan\./gi,
        "Included free on your plan permanently."
      )
      .replace(
        /Free for your first 12 months\./gi,
        "Included free on your plan permanently."
      )
      .replace(
        /Waived for your first 12 months, then \$799\/mo\.?/gi,
        "Included free on your plan permanently."
      );
  }

  if (softwarePricingMode === "paid_from_day_one") {
    return text
      .replace(
        /### 3\.\s*You're getting \$3,700\/mo of software for free\./gi,
        "### 3. You're getting $3,700/mo of software for $799/mo."
      )
      .replace(
        /You're getting \$3,700\/mo of software for free\./gi,
        "You're getting $3,700/mo of software for $799/mo."
      )
      .replace(
        /Included free for the first 12 months on your plan\./gi,
        "This plan includes a $799/mo software fee from month one."
      )
      .replace(
        /Free for your first 12 months\./gi,
        "This plan includes a $799/mo software fee from month one."
      )
      .replace(
        /Waived for your first 12 months, then \$799\/mo\.?/gi,
        "This plan includes a $799/mo software fee from month one."
      );
  }

  return text
    .replace(
      /Included free for the first 12 months on your plan\./gi,
      "Waived for your first 12 months, then $799/mo."
    )
    .replace(
      /Free for your first 12 months\./gi,
      "Waived for your first 12 months, then $799/mo."
    );
};

const QuoteYourPricingSlide = ({ practiceName, collections, patients, providers, planType, fixedPrice, fixedPriceMaxCollections, fixedPriceMaxPatients, waiveSoftwareFeeFirst12Months, softwareFreeAfter12Months, pricingPerspective, ashaPrice: precomputedPrice, ashaPriceLabel: precomputedLabel }) => {
  const isManaged = planType === "managed";
  const isFixed = planType === "fixed" && fixedPrice && fixedPrice > 0;
  const tierPerPatient = patients <= 300 ? 15 : patients <= 500 ? 12 : 8;
  const tierName = patients <= 300 ? "Startup" : patients <= 500 ? "Growth" : "Pro";
  const managedPrice = 799 + Math.round(collections * 0.03);
  const softwareFeeWaivedFirst12Months = softwareFreeAfter12Months || (
    typeof waiveSoftwareFeeFirst12Months === "boolean" ? waiveSoftwareFeeFirst12Months : true
  );
  const softwarePricingMode = softwareFreeAfter12Months
    ? "permanent_free"
    : softwareFeeWaivedFirst12Months
      ? "waived_first_year"
      : "paid_from_day_one";
  const softwarePricingSentence = softwarePricingMode === "permanent_free"
    ? "Included free on your plan permanently."
    : softwarePricingMode === "waived_first_year"
      ? "Waived for your first 12 months, then $799/mo."
      : "This plan includes a $799/mo software fee from month one.";
  const currentSoftwareFee = softwarePricingMode === "paid_from_day_one" ? 799 : 0;
  const softwarePricingHeading = softwarePricingMode === "paid_from_day_one"
    ? "### 3. You're getting $3,700/mo of software for $799/mo."
    : "### 3. You're getting $3,700/mo of software for free.";
  const softwarePricingCallout = softwarePricingMode === "permanent_free"
    ? "Software stays included at $0/mo after month 12 on this quote."
    : softwarePricingMode === "waived_first_year"
      ? "Software is waived for the first 12 months, then billed at $799/mo starting in month 13."
      : "This quote includes the $799/mo software fee from month one.";

  // Use pre-computed values from CRM if available
  const ashaPrice = precomputedPrice ?? (
    isFixed
      ? fixedPrice + currentSoftwareFee
      : isManaged
        ? managedPrice
        : patients * tierPerPatient
  );

  // For justification, use upper-limit values if provided
  const justifyCollections = (isFixed && fixedPriceMaxCollections) ? fixedPriceMaxCollections : collections;
  const justifyPatients = (isFixed && fixedPriceMaxPatients) ? fixedPriceMaxPatients : patients;
  const pctOfCollections = ((ashaPrice / justifyCollections) * 100).toFixed(2);
  const perPatientEffective = (ashaPrice / justifyPatients).toFixed(2);

  // Fallback piecemeal cost for fallback perspective
  const piecemealTotal = (() => {
    let billing = 0;
    if (collections <= 100000) billing = collections * 0.035;
    else if (collections <= 150000) billing = 100000 * 0.035 + (collections - 100000) * 0.03;
    else billing = 100000 * 0.035 + 50000 * 0.03 + (collections - 150000) * 0.025;
    const rcm = Math.round(billing) + patients * 8 + 1200;
    const sw = 3298 + Math.max(0, providers - 2) * 149;
    const labor = Math.round((collections / 100000) * 0.5 * 3500);
    return rcm + sw + labor;
  })();
  const savings = piecemealTotal - ashaPrice;

  const scale = collections / 100000;
  const ptScale = patients / 300;
  const prodConservative = Math.round(5000 * scale * (0.6 + ptScale * 0.4));
  const prodRealistic = Math.round(8000 * scale * (0.6 + ptScale * 0.4));

  // Build fallback perspective prose (with ### headings)
  const fallbackPerspective = `### 1. This is unbeatable cost, by far.
At **${pctOfCollections}%** of collections, you're paying less than half what billing companies charge (3.5–5%) — and they only handle claim submission and posting. To get the same coverage from other vendors would cost **$${piecemealTotal.toLocaleString()}/mo** — Asha saves you **$${savings.toLocaleString()}/mo**.

### 2. $${perPatientEffective}/pt to replace $120+ of human work.
Your staff spends 3–4 hours per patient on scheduling, insurance, claims, billing, recalls, and follow-ups. That's roughly **1/${Math.round(120 / Number(perPatientEffective))}th the cost** of a human — and the AI runs 24/7 with no sick days, turnover, or benefits.

${softwarePricingHeading}
PMS, communication suite, marketing, imaging, payments, eClaims, insurance verification — all worth ~**$3,700/mo** at published rates. ${softwarePricingSentence}

### 4. Asha is not a cost — it's a revenue multiplier.
Based on your practice's numbers, Asha is estimated to generate **$${prodConservative.toLocaleString()}–$${prodRealistic.toLocaleString()}/mo in additional production** from after-hours booking, reactivated hygiene patients, and improved case acceptance. The service pays for itself many times over.`;

  const perspectiveText = (pricingPerspective && pricingPerspective.trim().length > 50)
    ? normalizeSoftwarePricingCopy(pricingPerspective, softwarePricingMode)
    : fallbackPerspective;

  // Render inline bold markdown
  const renderInline = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <span key={i} className="font-bold text-slate-800">{part.slice(2, -2)}</span>;
      }
      return part;
    });
  };

  // Markdown renderer that handles ### headings and paragraphs
  const renderMarkdown = (text) => {
    // Split into individual lines, then process
    const lines = text.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
    const elements = [];
    let paragraphLines = [];
    let headingCount = 0;

    const flushParagraph = () => {
      if (paragraphLines.length > 0) {
        const content = paragraphLines.join(" ");
        elements.push(
          <p key={`p-${elements.length}`} className="text-sm text-slate-600 leading-relaxed mb-4">
            {renderInline(content)}
          </p>
        );
        paragraphLines = [];
      }
    };

    for (const line of lines) {
      if (line.startsWith("### ") || line.startsWith("## ")) {
        flushParagraph();
        headingCount++;
        let headingText = line.replace(/^#{2,3}\s*/, "");
        // Strip existing number prefix if present (e.g. "1. " or "1) ")
        headingText = headingText.replace(/^\d+[.)]\s*/, "");
        elements.push(
          <h4 key={`h-${elements.length}`} className="text-xl font-bold text-slate-900 mt-0 mb-1">
            <span className="text-blue-600">{headingCount}.</span>{" "}
            {renderInline(headingText)}
          </h4>
        );
      } else {
        paragraphLines.push(line);
      }
    }
    flushParagraph();

    return elements;
  };

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 lg:p-8 relative overflow-hidden font-sans">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="relative z-10 flex flex-col h-full w-full">
        {/* Header */}
        <div className="mb-5 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-2 border border-blue-100">
              <DollarSign className="w-4 h-4" />
              <span>Your Pricing — {practiceName}</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Let's put that price into <span className="text-blue-600">perspective</span>.
            </h2>
          </motion.div>
        </div>

        {/* Narrative — full width */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex-1 min-h-0 bg-white rounded-2xl border border-blue-200 shadow-sm p-6 flex flex-col"
        >
          {isFixed && (
            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex-shrink-0">
              <p className="text-sm font-semibold text-emerald-900">
                {softwarePricingCallout}
              </p>
            </div>
          )}

          {/* Rich text content — 2 column grid, each section stays together */}
          <div className="flex-1 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              {(() => {
                // Group elements into sections (heading + following paragraphs)
                const rendered = renderMarkdown(perspectiveText);
                const sections = [];
                let current = [];
                for (const el of rendered) {
                  if (el.type === 'h4' && current.length > 0) {
                    sections.push(current);
                    current = [];
                  }
                  current.push(el);
                }
                if (current.length > 0) sections.push(current);
                return sections.map((section, i) => (
                  <div key={i} className="break-inside-avoid">
                    {section}
                  </div>
                ));
              })()}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QuoteYourPricingSlide;
